import { useState } from "react";
import { ArrowLeft, ArrowRight, Ban, Clock, EyeOff, Loader2, Printer, Truck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { OrderActionsMenu } from "./OrderDetailHelpers";

export function OrderDetailHeader({
  order,
  orderIndex,
  totalOrders,
  onNavigate,
  orderNum,
  orderTypeColor,
  orderTypeIcon,
  orderTypeLabel,
  isFinished,
  isDelivery,
  deliverers,
  dispatching,
  handleQuickDispatch,
  handlePrint,
  canEditDetails,
  cancelling,
  openEditDialog,
  handleDuplicate,
  handleCreateInvoice,
  handleFinalize,
  setShowCancelDialog,
  setCancelReason,
  setShowPaymentDialog,
  setShowScheduleDialog,
  setScheduleFor,
}) {
  const [pendingDeliverer, setPendingDeliverer] = useState(null);
  const activeDeliverers = deliverers.filter(d => d.active !== false);

  return (
    <div className="px-3 sm:px-4 py-1.5 sm:py-2 border-b bg-gray-50 flex flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0 w-full">
      <div className="flex items-center gap-1 min-w-0 shrink-0">
        <button className="w-7 h-7 rounded flex items-center justify-center hover:bg-gray-200 disabled:opacity-30 shrink-0" onClick={() => onNavigate(-1)} disabled={orderIndex <= 0} data-testid="order-nav-prev"><ArrowLeft className="w-4 h-4 text-gray-500" /></button>
        <button className="w-7 h-7 rounded flex items-center justify-center hover:bg-gray-200 disabled:opacity-30 shrink-0" onClick={() => onNavigate(1)} disabled={orderIndex >= totalOrders - 1} data-testid="order-nav-next"><ArrowRight className="w-4 h-4 text-gray-500" /></button>
        <span className="font-bold text-gray-900 text-xs sm:text-sm shrink-0 whitespace-nowrap" data-testid="order-number">#{orderNum}</span>
        <Badge className={`${orderTypeColor} text-[10px] font-bold border shrink-0 whitespace-nowrap`}>{orderTypeIcon}<span className="ml-1">{orderTypeLabel}</span></Badge>
        {!isFinished && (() => {
          const elapsed = Math.floor((Date.now() - new Date(order.created_at)) / 60000);
          const isUrgent = elapsed > 30;
          const isWarn = elapsed > 15 && elapsed <= 30;
          return (
            <span className={`text-[10px] font-bold flex items-center gap-0.5 shrink-0 ${isUrgent ? "text-red-600" : isWarn ? "text-amber-600" : "text-gray-400"}`} data-testid="order-elapsed">
              <Clock className="w-3 h-3" />{elapsed}m
            </span>
          );
        })()}
      </div>
      <div className="flex items-center gap-1 ml-auto shrink-0 flex-wrap justify-end">
        {isDelivery && order.status === "ready" && !order.deliverer_id && deliverers.length > 0 && (
          <DropdownMenu onOpenChange={(open) => { if (open) console.log("[DELIVERER_LIST_OPENED]", { orderId: order.id, orderNum }); }}>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="gap-1 text-xs h-8 bg-purple-600 hover:bg-purple-700 text-white" disabled={dispatching} data-testid="dispatch-dropdown-btn">
                {dispatching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />}
                DESPACHAR
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={4} className="w-52 z-50" data-testid="dispatch-dropdown-menu">
              <DropdownMenuLabel className="text-[10px] text-gray-500 uppercase">Selecione o Entregador</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {activeDeliverers.length === 0 ? (
                <DropdownMenuItem disabled className="text-xs text-gray-400" data-testid="no-active-deliverers-msg">
                  Nenhum estafeta ativo disponível
                </DropdownMenuItem>
              ) : activeDeliverers.map(d => (
                <DropdownMenuItem key={d.id} onClick={() => { console.log("[DELIVERER_SELECTED]", { orderId: order.id, delivererId: d.id, name: d.name, status: d.status }); setPendingDeliverer(d); }} className="cursor-pointer gap-2 font-medium" data-testid={`dispatch-to-${d.id?.slice(-6)}`}>
                  <Truck className="w-3.5 h-3.5 text-purple-500" />
                  <span className="flex-1">{d.name}</span>
                  {d.status === "available"
                    ? <span className="text-[10px] text-emerald-500 font-bold">Livre</span>
                    : <span className="text-[10px] text-amber-500 font-bold">{d.status === "delivering" ? "Em entrega" : d.status}</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {isDelivery && order.status === "ready" && !order.deliverer_id && deliverers.length === 0 && (
          <span className="text-xs text-gray-400 font-medium" data-testid="no-deliverers-msg">
            Nenhum estafeta disponível
          </span>
        )}
        <AlertDialog open={!!pendingDeliverer} onOpenChange={(open) => { if (!open) setPendingDeliverer(null); }}>
          <AlertDialogContent data-testid="dispatch-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar despacho</AlertDialogTitle>
              <AlertDialogDescription>
                Despachar pedido para <strong>{pendingDeliverer?.name}</strong>?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setPendingDeliverer(null)} data-testid="dispatch-cancel-btn">Cancelar</AlertDialogCancel>
              <AlertDialogAction
                className="bg-purple-600 hover:bg-purple-700"
                disabled={dispatching}
                onClick={() => { handleQuickDispatch(pendingDeliverer.id, pendingDeliverer.name); setPendingDeliverer(null); }}
                data-testid="dispatch-confirm-btn"
              >
                {dispatching ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Confirmar despacho
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {order.hidden && <Badge className="bg-gray-200 text-gray-600 text-xs shrink-0"><EyeOff className="w-3 h-3 mr-1" />Oculto</Badge>}
        {order.status === "cancelled" && <Badge className="bg-red-100 text-red-700 text-xs gap-1 shrink-0"><Ban className="w-3 h-3" />Cancelado</Badge>}
        <Button variant="outline" size="sm" className="gap-1 text-xs h-7 sm:h-8 px-2 sm:px-3 shrink-0" onClick={handlePrint} data-testid="print-order-btn">
          <Printer className="w-3.5 h-3.5" /><span className="hidden sm:inline">IMPRIMIR</span>
        </Button>
        <OrderActionsMenu
          order={order}
          canEditDetails={canEditDetails}
          isDelivery={isDelivery}
          isFinished={isFinished}
          cancelling={cancelling}
          openEditDialog={openEditDialog}
          handleDuplicate={handleDuplicate}
          handlePrint={handlePrint}
          handleCreateInvoice={handleCreateInvoice}
          handleFinalize={handleFinalize}
          setShowCancelDialog={setShowCancelDialog}
          setCancelReason={setCancelReason}
          setShowPaymentDialog={setShowPaymentDialog}
          setShowScheduleDialog={setShowScheduleDialog}
          setScheduleFor={setScheduleFor}
        />
      </div>
    </div>
  );
}
